/**
 * Failed sign-in counter (TC-LOGIN-004).
 *
 * Six wrong passwords in a row cost an attacker nothing today: no lockout, no
 * cooldown, no CAPTCHA. This is the client half of the answer — after
 * `CAPTCHA_AFTER_FAILURES` failures the form starts attaching a reCAPTCHA
 * Enterprise token to every attempt, so the backend can score the traffic and
 * refuse the ones that look automated.
 *
 * It is a speed bump, not a control: localStorage is trivially cleared, and a
 * script that never opens the page never reads this file at all. Rate limiting by
 * mobile number and by IP still belongs on the backend — this only makes sure a
 * token is there for the backend to use, and tells the customer what changed.
 */
const WINDOW_MS = 15 * 60 * 1000

/** The 6th attempt is the first one carrying a token. */
export const CAPTCHA_AFTER_FAILURES = 5

/** Counted per number *and* per device: rotating the phone number through the same
 *  form must not reset the count back to zero. */
const DEVICE = '__device'

interface Attempts {
  count: number
  /** Epoch ms of the last failure; the window is rolling. */
  at: number
}

const KEY = (slug: string, subject: string) => `vs.signin.fails.${slug}.${subject}`

function read(slug: string, subject: string): number {
  try {
    const raw = localStorage.getItem(KEY(slug, subject))
    if (!raw) return 0
    const stored = JSON.parse(raw) as Attempts
    if (!stored || typeof stored.count !== 'number') return 0
    if (Date.now() - stored.at > WINDOW_MS) {
      localStorage.removeItem(KEY(slug, subject))
      return 0
    }
    return stored.count
  } catch {
    return 0
  }
}

function bump(slug: string, subject: string): number {
  const next = read(slug, subject) + 1
  try {
    localStorage.setItem(KEY(slug, subject), JSON.stringify({ count: next, at: Date.now() } satisfies Attempts))
  } catch {
    /* private mode — the counter simply does not survive */
  }
  return next
}

/** How many failures count against this attempt: the worse of the two counters. */
export function failedAttempts(slug: string, mobile: string): number {
  return Math.max(read(slug, DEVICE), mobile ? read(slug, mobile) : 0)
}

/** True once the form should start sending a reCAPTCHA token. */
export function needsCaptcha(slug: string, mobile: string): boolean {
  return failedAttempts(slug, mobile) >= CAPTCHA_AFTER_FAILURES
}

/** Records one rejected sign-in and returns the new count. */
export function recordFailure(slug: string, mobile: string): number {
  const device = bump(slug, DEVICE)
  const perNumber = mobile ? bump(slug, mobile) : 0
  return Math.max(device, perNumber)
}

/** A successful sign-in clears the number, and the device with it. */
export function clearFailures(slug: string, mobile: string) {
  try {
    localStorage.removeItem(KEY(slug, DEVICE))
    if (mobile) localStorage.removeItem(KEY(slug, mobile))
  } catch {
    /* ignore */
  }
}
